import type { TranslationModule } from '../types';

export const publicCasesTranslations: TranslationModule = {
  ro: {
    seoTitle: "Cazuri Asemănătoare și Spețe Similare | Legea Aplicată",
    seoDesc: "Descrie situația ta și găsește cazuri asemănătoare și spețe similare din România. Compară faptele, dovezile și hotărârile, cu explicații pe înțelesul tău.",
    eyebrow: "Pentru tine, chiar dacă nu ai studiat dreptul",
    heading: "Găsește cazuri și spețe asemănătoare situației tale",
    intro: "Descrie în cuvintele tale ce s-a întâmplat și caută cazuri juridice asemănătoare. Poți citi situația din alte procese, dovezile analizate și ce a decis instanța, în materialele disponibile. Nu trebuie să cunoști termeni de drept: o speță este un caz concret, cu propriile fapte și împrejurări. Asemănarea te ajută să te orientezi, dar nu înseamnă că vei obține același rezultat.",
    platformContext: "Pentru legislație, jurisprudență și alte instrumente de documentare, explorează",
    platformLinkLabel: "platforma Legea Aplicată",
    guideLink: "Cum te ajută, pas cu pas",
    professionalPrompt: "Lucrezi în domeniul juridic?",
    professionalDesc: "Explorează legislația și jurisprudența cu instrumente avansate.",
    professionalAction: "Căutare avansată",
    professionalAria: "Căutare avansată pentru profesioniști",
    
    // Workspace
    workspaceAria: "Caută cazuri asemănătoare",
    step1Title: "Cu ce problemă te confrunți?",
    domainLegend: "Tipul problemei",
    domainCivil: "Problemă civilă",
    domainCivilDesc: "Bani, familie, locuință, muncă",
    domainPenal: "Problemă penală",
    domainPenalDesc: "Acuzații, infracțiuni, victime",
    modeGroupAria: "Cum vrei să cauți?",
    modeKeywords: "Cuvinte-cheie",
    modeFactual: "Povestește ce s-a întâmplat",
    inputTitle: "Descrie situația ta",
    inputSubtitle: "Scrie pe scurt ce s-a întâmplat sau adaugă cuvinte-cheie.",
    placeholderKeywords: "De exemplu: bani împrumutați și nerestituiți…",
    placeholderFactual: "Povestește în cuvintele tale: ce s-a întâmplat, când, ce dovezi ai și ce vrei să lămurești…",
    helpKeywords: "Câteva cuvinte sunt suficiente. Poți și să atașezi un document sau să dictezi.",
    helpFactual: "Scrie între 200 și 8.000 de caractere. Ai scris {count}. Poți atașa un document sau dicta.",
    examplesTitle: "Poți încerca:",
    accessAdmin: "Pentru căutarea după descriere poți încărca mai multe cazuri pe măsură ce le parcurgi.",
    accessUser: "Până la {cap} rezultate pentru {account}. Se aplică limitele de căutare și acces ale platformei.",
    rolePro: "contul Pro",
    roleBasic: "contul Basic",
    roleGuest: "vizitatori fără cont",
    viewAccounts: "Vezi conturile",
    createAccount: "Creează un cont",

    // Benefits aside
    benefitsKicker: "Mai multă claritate, pas cu pas",
    benefitsHeadingCivil: "Înțelege ce poate conta în procesul tău civil.",
    benefitsHeadingPenal: "Înțelege cum sunt analizate cazurile penale.",
    benefitNote: "Pornești de la cazuri și hotărâri existente. Textele originale pot conține termeni de drept; ghidul te ajută să le parcurgi.",

    // Results
    resultsAria: "Rezultatele căutării",
    resultsKicker: "Pasul 2 · Compară poveștile",
    searchingCases: "Căutăm cazuri pentru tine…",
    caseFound: "{count} caz găsit",
    casesFound: "{count} cazuri găsite",
    badgeCivil: "Probleme civile",
    badgePenal: "Probleme penale",
    forQuery: "Pentru: {query}",
    quotaHelp: "Căutări rămase în limita curentă: {remaining}{limitText}.",
    resultsTip: "Citește ce s-a întâmplat, apoi deschide un caz apropiat de situația ta. Poți compara și alte informații:",
    viewSelectorLabel: "Ce vrei să vezi în rezultate?",
    loadingFactual: "Comparăm descrierea ta cu situațiile din cazurile disponibile. Poate dura puțin.",
    loadingDefault: "Se încarcă rezultatele…",
    emptyTitle: "Nu am găsit cazuri pentru această căutare.",
    emptyDesc: "Încearcă alte cuvinte sau descrie mai clar ce s-a întâmplat. Poți schimba și tipul problemei.",
    loadMore: "Arată mai multe cazuri",
    modalLoading: "Se deschid detaliile cazului…",

    // Share
    shareGroupAria: "Distribuie pagina de cazuri asemănătoare",
    shareLabel: "Distribuie",
    copyLink: "Copiază linkul",
    copiedStatus: "Linkul pentru distribuire a fost copiat.",
    copyFallback: "Copiază acest link: {url}",

    // Advisor
    advisorEyebrow: "O perspectivă de avocat",
    advisorHeading: "Primul pas este să înțelegi.",
    advisorMessage: "Legea Aplicată pune în centrul său dreptul în forma în care acesta contează cu adevărat: în interpretare, în practică și în efectele sale concrete. Legislația, jurisprudența și problematica juridică actuală sunt reunite într-un demers destinat celor care studiază, profesează sau se confruntă cu aplicarea dreptului.",
    advisorAction: "Găsește un caz asemănător",
    advisorRole: "Avocat · Partener LegeaAplicata.ro",

    // How it works
    howKicker: "De la întrebări la idei utile",
    howHeading: "Cum găsești cazuri asemănătoare",
    contextNote: "Un caz asemănător este un punct de plecare. Compară și diferențele, verifică regulile aplicabile și discută ideile cu un avocat. Rezultatul altui proces nu îl garantează pe al tău.",
    step1TitleGuide: "Spune ce ți s-a întâmplat",
    step1TextGuide: "Scrie câteva cuvinte importante sau povestește situația: ce s-a întâmplat, când și ce vrei să lămurești.",
    step2TitleGuide: "Găsește o poveste apropiată de a ta",
    step2TextGuide: "Citește ce s-a întâmplat în fiecare caz. Compară împrejurările și dovezile, nu doar titlul.",
    step3TitleGuide: "Vezi ce te poate ajuta",
    step3TextGuide: "Deschide cazul. Urmărește decizia și explicațiile judecătorului și notează ideile relevante pentru situația ta.",

    // FAQ
    faqKicker: "Pe înțelesul tău",
    faqHeading: "Întrebări firești, răspunsuri clare.",
    faq1Q: "Trebuie să cunosc termeni de drept?",
    faq1A: "Nu. Poți scrie în cuvintele tale. Alege căutarea după cuvinte-cheie pentru o problemă scurtă sau descrierea situației pentru a explica împrejurările mai pe larg.",
    faq2Q: "Cum mă ajută într-un proces civil?",
    faq2A: "Poți compara probleme legate de bani, contracte, locuință, familie sau muncă. În cazurile disponibile vezi ce dovezi au contat, de ce au fost acceptate sau respinse cererile și ce a decis instanța.",
    faq3Q: "Cum mă ajută într-un proces penal?",
    faq3A: "Poți vedea cum au fost analizate fapte și dovezi asemănătoare, ce rezultat a avut procesul și, unde sunt indicate, ce pedepse au fost aplicate și ce împrejurări au influențat decizia.",
    faq4Q: "Pot folosi ideile dintr-un caz în procesul meu?",
    faq4A: "Le poți folosi ca punct de plecare: verifică asemănările, diferențele, dovezile și regulile aplicabile. O hotărâre din alt caz nu garantează același rezultat și nu înlocuiește analiza unui avocat.",
    faq5Q: "Ce pot vedea fără un cont?",
    faq5A: "Poți începe căutarea fără cont. Numărul de rezultate, căutările disponibile și accesul la detalii respectă aceleași limite ale conturilor din platformă. Limitele relevante sunt afișate când cauți.",

    // Trust
    trustHeading: "Surse și informații despre platformă",
    trustText: "Verifică proveniența materialelor, condițiile de acces și modul în care sunt prelucrate datele tale. Consultă informațiile despre echipă și termenii platformei pentru detalii despre operator.",
    trustNavAria: "Surse și informații despre platformă",
    trustLinkDataSources: "Sursele informațiilor juridice",
    trustLinkAbout: "Despre noi",
    trustLinkContact: "Contact",
    trustLinkTerms: "Termeni și condiții",
    trustLinkPrivacy: "Politica de confidențialitate",
    trustLinkPlans: "Conturi și limite"
  },
  en: {
    seoTitle: "Similar Cases and Comparable Rulings | Legea Aplicată",
    seoDesc: "Describe your situation and find similar cases and comparable rulings from Romania. Compare facts, evidence, and court decisions in plain language.",
    eyebrow: "For you, even if you haven't studied law",
    heading: "Find cases and judicial rulings similar to your situation",
    intro: "Describe what happened in your own words and search for comparable legal cases. Read what happened in other lawsuits, the evidence reviewed, and what the court ruled in available records. You don't need legal jargon: a lawsuit is a specific case with its own facts and circumstances. Similarities help guide you, but they do not guarantee the same outcome.",
    platformContext: "For legislation, case law, and other legal research tools, explore",
    platformLinkLabel: "the Legea Aplicată platform",
    guideLink: "How it helps you, step by step",
    professionalPrompt: "Working in the legal profession?",
    professionalDesc: "Explore legislation and jurisprudence with advanced tools.",
    professionalAction: "Advanced search",
    professionalAria: "Advanced search for legal professionals",
    
    // Workspace
    workspaceAria: "Search similar cases",
    step1Title: "What issue are you facing?",
    domainLegend: "Type of issue",
    domainCivil: "Civil matter",
    domainCivilDesc: "Money, family, housing, employment",
    domainPenal: "Criminal matter",
    domainPenalDesc: "Charges, criminal offenses, victims",
    modeGroupAria: "How would you like to search?",
    modeKeywords: "Keywords",
    modeFactual: "Tell what happened",
    inputTitle: "Describe your situation",
    inputSubtitle: "Briefly write what happened or add keywords.",
    placeholderKeywords: "For example: borrowed money not repaid…",
    placeholderFactual: "Tell in your own words: what happened, when, what evidence you have, and what you want clarified…",
    helpKeywords: "A few words are enough. You can also attach a document or dictate.",
    helpFactual: "Write between 200 and 8,000 characters. You wrote {count}. You can attach a document or dictate.",
    examplesTitle: "You can try:",
    accessAdmin: "For description-based search you can load more cases as you browse.",
    accessUser: "Up to {cap} results for {account}. Platform search and access limits apply.",
    rolePro: "Pro account",
    roleBasic: "Basic account",
    roleGuest: "guest visitors",
    viewAccounts: "View plans",
    createAccount: "Create an account",

    // Benefits aside
    benefitsKicker: "Greater clarity, step by step",
    benefitsHeadingCivil: "Understand what may matter in your civil lawsuit.",
    benefitsHeadingPenal: "Understand how criminal cases are evaluated.",
    benefitNote: "You start from existing cases and judgments. Primary texts may include legal terms; the guide helps you navigate them.",

    // Results
    resultsAria: "Search results",
    resultsKicker: "Step 2 · Compare the stories",
    searchingCases: "Searching cases for you…",
    caseFound: "{count} case found",
    casesFound: "{count} cases found",
    badgeCivil: "Civil matters",
    badgePenal: "Criminal matters",
    forQuery: "For: {query}",
    quotaHelp: "Searches remaining in current limit: {remaining}{limitText}.",
    resultsTip: "Read what happened, then open a case close to your situation. You can also compare other details:",
    viewSelectorLabel: "What do you want to see in results?",
    loadingFactual: "Comparing your description with scenarios in available cases. This may take a moment.",
    loadingDefault: "Loading results…",
    emptyTitle: "No cases found for this search.",
    emptyDesc: "Try different words or clarify what happened. You can also change the problem type.",
    loadMore: "Show more cases",
    modalLoading: "Opening case details…",

    // Share
    shareGroupAria: "Share similar cases page",
    shareLabel: "Share",
    copyLink: "Copy link",
    copiedStatus: "Share link copied to clipboard.",
    copyFallback: "Copy this link: {url}",

    // Advisor
    advisorEyebrow: "An attorney's perspective",
    advisorHeading: "The first step is understanding.",
    advisorMessage: "Legea Aplicată focuses on law in the form that truly matters: in its interpretation, its practice, and its tangible effects. Legislation, case law, and contemporary legal issues are brought together for those studying, practicing, or navigating the application of the law.",
    advisorAction: "Find a similar case",
    advisorRole: "Attorney at Law · Partner LegeaAplicata.ro",

    // How it works
    howKicker: "From questions to actionable insights",
    howHeading: "How to find similar cases",
    contextNote: "A similar case is a starting point. Compare the differences, verify applicable rules, and discuss your takeaways with an attorney. Another lawsuit's outcome never guarantees yours.",
    step1TitleGuide: "Tell what happened to you",
    step1TextGuide: "Write a few key words or explain your situation: what happened, when, and what you want clarified.",
    step2TitleGuide: "Find a case close to yours",
    step2TextGuide: "Read what happened in each case. Compare the facts and evidence, not just the case title.",
    step3TitleGuide: "See what can help you",
    step3TextGuide: "Open the case. Review the judge's ruling and reasoning, noting relevant ideas for your situation.",

    // FAQ
    faqKicker: "In plain terms",
    faqHeading: "Common questions, clear answers.",
    faq1Q: "Do I need to know legal terminology?",
    faq1A: "No. You can write in your own everyday words. Choose keyword search for a short query or situation description to explain circumstances in greater detail.",
    faq2Q: "How does this help me in a civil lawsuit?",
    faq2A: "You can compare issues regarding debt, contracts, housing, family, or employment. In available cases, see what evidence proved decisive, why claims were granted or denied, and what the court ruled.",
    faq3Q: "How does this help me in criminal proceedings?",
    faq3A: "You can see how comparable acts and evidence were examined, the verdict rendered, and, where specified, the penalties imposed and the factors influencing the sentencing.",
    faq4Q: "Can I use insights from a case in my own lawsuit?",
    faq4A: "You can use them as a starting point: check similarities, differences, proof, and applicable statutes. A judgment in another case never guarantees the same result and cannot replace legal counsel.",
    faq5Q: "What can I see without an account?",
    faq5A: "You can start searching without an account. The number of results, available searches, and access to details follow the platform's tier limits. Relevant limits are displayed when searching.",

    // Trust
    trustHeading: "Sources and platform information",
    trustText: "Verify material origins, access terms, and how your data is handled. Review team details and platform terms for information about the operator.",
    trustNavAria: "Sources and platform information",
    trustLinkDataSources: "Legal data sources",
    trustLinkAbout: "About us",
    trustLinkContact: "Contact",
    trustLinkTerms: "Terms & Conditions",
    trustLinkPrivacy: "Privacy Policy",
    trustLinkPlans: "Plans and limits"
  },
  fr: {
    seoTitle: "Cas Similaires et Jurisprudence Comparable | Legea Aplicată",
    seoDesc: "Décrivez votre situation et trouvez des cas similaires et une jurisprudence comparable en Roumanie. Comparez les faits, les preuves et les jugements, expliqués simplement.",
    eyebrow: "Pour vous, même sans formation juridique",
    heading: "Trouvez des affaires et des jugements similaires à votre situation",
    intro: "Décrivez ce qui s'est passé avec vos propres mots et recherchez des affaires judiciaires comparables. Consultez les faits d'autres procès, les preuves examinées et le jugement rendu dans les dossiers disponibles. Nul besoin de maîtriser le jargon juridique : chaque affaire a ses faits propres. La similitude vous oriente, sans garantir le même résultat.",
    platformContext: "Pour la législation, la jurisprudence et d'autres outils de recherche, découvrez",
    platformLinkLabel: "la plateforme Legea Aplicată",
    guideLink: "Comment cela vous aide, étape par étape",
    professionalPrompt: "Vous travaillez dans le domaine juridique ?",
    professionalDesc: "Explorez les textes et la jurisprudence avec des outils avancés.",
    professionalAction: "Recherche avancée",
    professionalAria: "Recherche avancée pour professionnels du droit",
    
    // Workspace
    workspaceAria: "Rechercher des cas similaires",
    step1Title: "À quel problème êtes-vous confronté ?",
    domainLegend: "Type de litige",
    domainCivil: "Affaire civile",
    domainCivilDesc: "Argent, famille, logement, travail",
    domainPenal: "Affaire pénale",
    domainPenalDesc: "Accusations, infractions, victimes",
    modeGroupAria: "Comment souhaitez-vous chercher ?",
    modeKeywords: "Mots-clés",
    modeFactual: "Racontez ce qui s'est passé",
    inputTitle: "Décrivez votre situation",
    inputSubtitle: "Résumez ce qui s'est passé ou saisissez des mots-clés.",
    placeholderKeywords: "Par exemple : prêt d'argent non remboursé…",
    placeholderFactual: "Racontez avec vos propres mots : ce qui s'est passé, quand, quelles preuves vous avez et ce que vous souhaitez éclaircir…",
    helpKeywords: "Quelques mots suffisent. Vous pouvez également joindre un document ou dicter.",
    helpFactual: "Écrivez entre 200 et 8 000 caractères. Vous avez écrit {count}. Vous pouvez joindre un document ou dicter.",
    examplesTitle: "Vous pouvez essayer :",
    accessAdmin: "Pour la recherche par description, vous pouvez charger d'autres affaires au fur et à mesure.",
    accessUser: "Jusqu'à {cap} résultats pour {account}. Les limites de recherche et d'accès de la plateforme s'appliquent.",
    rolePro: "compte Pro",
    roleBasic: "compte Basic",
    roleGuest: "visiteurs sans compte",
    viewAccounts: "Voir les formules",
    createAccount: "Créer un compte",

    // Benefits aside
    benefitsKicker: "Plus de clarté, étape par étape",
    benefitsHeadingCivil: "Comprenez ce qui peut peser dans votre procès civil.",
    benefitsHeadingPenal: "Comprenez comment les affaires pénales sont analysées.",
    benefitNote: "Vous partez de cas et de jugements réels. Les textes intégraux peuvent contenir du jargon ; ce guide vous aide à les parcourir.",

    // Results
    resultsAria: "Résultats de recherche",
    resultsKicker: "Étape 2 · Comparez les histoires",
    searchingCases: "Recherche de dossiers en cours…",
    caseFound: "{count} affaire trouvée",
    casesFound: "{count} affaires trouvées",
    badgeCivil: "Affaires civiles",
    badgePenal: "Affaires pénales",
    forQuery: "Pour : {query}",
    quotaHelp: "Recherches restantes dans la limite actuelle : {remaining}{limitText}.",
    resultsTip: "Lisez ce qui s'est passé, puis ouvrez une affaire proche de votre situation. Vous pouvez comparer d'autres informations :",
    viewSelectorLabel: "Que souhaitez-vous afficher dans les résultats ?",
    loadingFactual: "Comparaison de votre description avec les affaires disponibles. Cela peut prendre un instant.",
    loadingDefault: "Chargement des résultats…",
    emptyTitle: "Aucun dossier trouvé pour cette recherche.",
    emptyDesc: "Essayez d'autres termes ou précisez ce qui s'est passé. Vous pouvez aussi changer de domaine.",
    loadMore: "Afficher plus d'affaires",
    modalLoading: "Ouverture des détails du dossier…",

    // Share
    shareGroupAria: "Partager la page des cas similaires",
    shareLabel: "Partager",
    copyLink: "Copier le lien",
    copiedStatus: "Lien de partage copié dans le presse-papiers.",
    copyFallback: "Copiez ce lien : {url}",

    // Advisor
    advisorEyebrow: "Le regard d'un avocat",
    advisorHeading: "La première étape est de comprendre.",
    advisorMessage: "Legea Aplicată place au cœur de son approche le droit tel qu'il compte réellement : dans son interprétation, sa pratique et ses effets concrets. Législation, jurisprudence et enjeux juridiques actuels sont réunis pour ceux qui étudient, exercent ou sont confrontés à l'application du droit.",
    advisorAction: "Trouver un cas similaire",
    advisorRole: "Avocate · Partenaire LegeaAplicata.ro",

    // How it works
    howKicker: "De vos questions à des pistes utiles",
    howHeading: "Comment trouver des affaires similaires",
    contextNote: "Un cas similaire est un point de départ. Comparez les différences, vérifiez les règles applicables et échangez avec un avocat. L'issue d'un autre procès ne garantit jamais la vôtre.",
    step1TitleGuide: "Expliquez ce qui vous est arrivé",
    step1TextGuide: "Saisissez quelques mots clés ou racontez votre litige : ce qui s'est passé, quand et ce que vous souhaitez éclaircir.",
    step2TitleGuide: "Trouvez une histoire proche de la vôtre",
    step2TextGuide: "Lisez les faits de chaque affaire. Comparez les circonstances et les preuves, pas seulement l'intitulé.",
    step3TitleGuide: "Identifiez les éléments utiles",
    step3TextGuide: "Ouvrez le dossier. Suivez la décision et les motivations du juge, et notez les points clés pour votre situation.",

    // FAQ
    faqKicker: "En termes clairs",
    faqHeading: "Questions fréquentes, réponses claires.",
    faq1Q: "Dois-je connaître les termes juridiques ?",
    faq1A: "Non. Vous pouvez écrire avec vos propres mots. Choisissez la recherche par mots-clés pour un problème concis ou la description de votre situation pour expliquer les faits en détail.",
    faq2Q: "Comment cela m'aide-t-il dans un litige civil ?",
    faq2A: "Vous pouvez comparer des litiges de créances, contrats, logement, famille ou droit du travail. Dans les dossiers disponibles, vous voyez quelles preuves ont été déterminantes, pourquoi les demandes ont été acceptées ou rejetées et ce que le juge a tranché.",
    faq3Q: "Comment cela m'aide-t-il dans une procédure pénale ?",
    faq3A: "Vous pouvez voir comment des infractions et preuves similaires ont été analysées, quelle a été l'issue du procès et, lorsqu'elles sont indiquées, quelles peines ont été prononcées et quels facteurs ont pesé.",
    faq4Q: "Puis-je utiliser les enseignements d'une affaire dans mon propre procès ?",
    faq4A: "Vous pouvez les utiliser comme point de départ : vérifiez les similitudes, les divergences, les preuves et les règles applicables. Un jugement rendu dans une autre affaire ne garantit jamais le même résultat et ne remplace pas l'analyse d'un avocat.",
    faq5Q: "Que puis-je consulter sans créer de compte ?",
    faq5A: "Vous pouvez lancer une recherche sans compte. Le volume de résultats, le nombre de requêtes et l'accès aux détails suivent les plafonds de la plateforme, affichés lors de votre recherche.",

    // Trust
    trustHeading: "Sources et informations sur la plateforme",
    trustText: "Vérifiez l'origine des sources, les conditions d'accès et le traitement de vos données. Consultez les informations sur l'équipe et les conditions de la plateforme.",
    trustNavAria: "Sources et informations sur la plateforme",
    trustLinkDataSources: "Sources des données juridiques",
    trustLinkAbout: "À propos",
    trustLinkContact: "Contact",
    trustLinkTerms: "Conditions générales",
    trustLinkPrivacy: "Politique de confidentialité",
    trustLinkPlans: "Formules et limites"
  }
};

export default publicCasesTranslations;
